import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  PlusIcon,
  EyeIcon,
  UserPlusIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CloudArrowDownIcon,
} from '@heroicons/react/24/outline';
import AccountSettingsModal from './AccountSettingsModal';
import UsageBreakdownModal from './UsageBreakdownModal';
import KeystoneSyncModal from './KeystoneSyncModal';

interface Account {
  id: number;
  slug: string;
  name: string;
  settings: Record<string, unknown>;
  user_count: number;
  is_platform?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AccountManagement: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create-account modal
  const [showCreate, setShowCreate] = useState(false);
  const [createSlug, setCreateSlug] = useState('');
  const [createName, setCreateName] = useState('');
  const [createSettings, setCreateSettings] = useState('{}');
  const [createBusy, setCreateBusy] = useState(false);

  // Create-user modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserAccountId, setNewUserAccountId] = useState<number | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [createUserBusy, setCreateUserBusy] = useState(false);

  // Settings modal
  const [settingsAccount, setSettingsAccount] = useState<Account | null>(null);
  const [usageAccount, setUsageAccount] = useState<Account | null>(null);
  const [keystoneAccount, setKeystoneAccount] = useState<Account | null>(null);

  const loadAccounts = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/accounts`, { headers: authHeaders() });
      setAccounts(res.data);
    } catch (e) {
      console.error('Failed to load accounts', e);
      toast.error('Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const submitCreateAccount = async () => {
    if (!createSlug || !createName) {
      toast.error('Slug and name are required');
      return;
    }
    let parsedSettings: Record<string, unknown> = {};
    try {
      parsedSettings = JSON.parse(createSettings || '{}');
    } catch {
      toast.error('Settings must be valid JSON');
      return;
    }
    setCreateBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/admin/accounts`,
        { slug: createSlug, name: createName, settings: parsedSettings },
        { headers: authHeaders() }
      );
      toast.success(`Account ${createName} created`);
      setShowCreate(false);
      setCreateSlug('');
      setCreateName('');
      setCreateSettings('{}');
      await loadAccounts();
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed';
      toast.error(`Create failed: ${msg}`);
    } finally {
      setCreateBusy(false);
    }
  };

  const submitCreateUser = async () => {
    if (!newUserAccountId || !newUsername || !newEmail || !newPassword) {
      toast.error('All fields required');
      return;
    }
    setCreateUserBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/admin/users`,
        {
          username: newUsername,
          email: newEmail,
          password: newPassword,
          full_name: newFullName || null,
          account_id: newUserAccountId,
          is_admin: newIsAdmin,
          is_super_admin: false,
        },
        { headers: authHeaders() }
      );
      toast.success(`User ${newUsername} created`);
      setShowCreateUser(false);
      setNewUsername('');
      setNewEmail('');
      setNewPassword('');
      setNewFullName('');
      setNewIsAdmin(false);
      setNewUserAccountId(null);
      await loadAccounts();
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed';
      toast.error(`Create user failed: ${msg}`);
    } finally {
      setCreateUserBusy(false);
    }
  };

  const startImpersonation = async (account: Account) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/admin/impersonate/${account.id}`,
        {},
        { headers: authHeaders() }
      );
      const { access_token, impersonating_account_name } = res.data;

      // Save the actor's original token + cached user so we can restore on exit.
      const originalToken = localStorage.getItem('auth_token');
      const originalUser = localStorage.getItem('auth_user');
      if (originalToken) localStorage.setItem('auth_token_original', originalToken);
      if (originalUser) localStorage.setItem('auth_user_original', originalUser);

      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('impersonating_account_name', impersonating_account_name);

      // Refetch /auth/me with the impersonation token so the cached user
      // identity reflects the impersonated tenant (account, is_super_admin,
      // settings). AdminPanel and InvoiceEditForm key off this on next load.
      const me = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      localStorage.setItem(
        'auth_user',
        JSON.stringify({
          username: me.data.username,
          email: me.data.email,
          is_admin: me.data.is_admin ?? false,
          is_super_admin: me.data.is_super_admin ?? false,
          customer_group: me.data.customer_group,
          account: me.data.account ?? null,
        })
      );

      toast.success(`Now viewing as ${impersonating_account_name}. Reloading…`);
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed';
      toast.error(`Impersonate failed: ${msg}`);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BuildingOfficeIcon className="h-5 w-5 text-brand-600" />
          <h2 className="text-lg font-semibold dark:text-white">Accounts (Tenants)</h2>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-md"
        >
          <PlusIcon className="h-4 w-4" />
          New Account
        </button>
      </div>

      {/* Impersonation Exit lives in the global ImpersonationBanner so it's
          visible even while AccountManagement is hidden (super-admin only). */}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading accounts…</p>
      ) : accounts.length === 0 ? (
        <p className="text-sm text-gray-500">No accounts yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500 border-b">
            <tr>
              <th className="text-left py-2">ID</th>
              <th className="text-left py-2">Slug</th>
              <th className="text-left py-2">Name</th>
              <th className="text-left py-2">Users</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...accounts]
              .sort((a, b) => a.id - b.id)
              .map((a) => (
              <tr key={a.id} className="border-b dark:border-gray-700">
                <td className="py-2 dark:text-gray-200">{a.id}</td>
                <td className="py-2 dark:text-gray-200 font-mono text-xs">{a.slug}</td>
                <td className="py-2 dark:text-gray-200">{a.name}</td>
                <td className="py-2 dark:text-gray-200">{a.user_count}</td>
                <td className="py-2 text-right space-x-1">
                  <button
                    onClick={() => setUsageAccount(a)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded"
                    title="View API usage + cost"
                  >
                    <ChartBarIcon className="h-3 w-3" />
                    Usage
                  </button>
                  {(a.settings as Record<string, unknown>)?.keystone_customer_code ? (
                    <button
                      onClick={() => setKeystoneAccount(a)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded"
                      title="Keystone sync status + manual refresh"
                    >
                      <CloudArrowDownIcon className="h-3 w-3" />
                      Keystone
                    </button>
                  ) : null}
                  <button
                    onClick={() => setSettingsAccount(a)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded"
                    title="Edit settings"
                  >
                    <Cog6ToothIcon className="h-3 w-3" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      setNewUserAccountId(a.id);
                      setShowCreateUser(true);
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded"
                    title="Create user in this account"
                  >
                    <UserPlusIcon className="h-3 w-3" />
                    User
                  </button>
                  <button
                    onClick={() => startImpersonation(a)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-brand-100 hover:bg-brand-200 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200 rounded"
                  >
                    <EyeIcon className="h-3 w-3" />
                    View as
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {settingsAccount && (
        <AccountSettingsModal
          account={settingsAccount}
          onClose={() => setSettingsAccount(null)}
          onSaved={loadAccounts}
        />
      )}

      {usageAccount && (
        <UsageBreakdownModal
          mode="account"
          targetId={usageAccount.id}
          targetLabel={usageAccount.name}
          onClose={() => setUsageAccount(null)}
        />
      )}

      {keystoneAccount && (
        <KeystoneSyncModal
          accountId={keystoneAccount.id}
          accountLabel={keystoneAccount.name}
          onClose={() => setKeystoneAccount(null)}
        />
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">Create Account</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Slug (lowercase, hyphens; e.g. wise-county-va)
                </label>
                <input
                  type="text"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Display Name</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Settings (JSON — gl_format_regex, default_currency, etc.)
                </label>
                <textarea
                  value={createSettings}
                  onChange={(e) => setCreateSettings(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 text-xs font-mono border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCreate(false)}
                className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 dark:text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={submitCreateAccount}
                disabled={createBusy}
                className="px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-50"
              >
                {createBusy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4 dark:text-white">
              Create User in {accounts.find((a) => a.id === newUserAccountId)?.name}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Username</label>
                <input
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Full Name</label>
                <input
                  type="text"
                  value={newFullName}
                  onChange={(e) => setNewFullName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white"
                />
              </div>
              <label className="flex items-center gap-2 text-sm dark:text-white">
                <input
                  type="checkbox"
                  checked={newIsAdmin}
                  onChange={(e) => setNewIsAdmin(e.target.checked)}
                />
                Account admin (can manage their own tenant's users)
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowCreateUser(false)}
                className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 dark:text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={submitCreateUser}
                disabled={createUserBusy}
                className="px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-50"
              >
                {createUserBusy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountManagement;
